/**
 * §billing.platform (2026-06-04) — tenant-facing PLATFORM subscription client.
 *
 * This is the subscription the tenant pays XPAND (Starter/Growth/Scale/…), NOT
 * the per-order payment links the tenant sends THEIR own customers. Status is
 * driven by Stripe via the platform webhook; card management happens on
 * Stripe's hosted Billing Portal (no card data touches the dashboard).
 *
 * Honest empties: `status === ""` means no subscription on file yet (a
 * grandfathered / pre-billing workspace) — never rendered as "paid".
 */
import { api } from "@/lib/api";

/** Raw Stripe subscription.status we persist verbatim. "" = none on file. */
export type SubscriptionStatus =
  | ""
  | "trialing"
  | "active"
  | "past_due"
  | "unpaid"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "paused";

export interface SubscriptionInfo {
  plan: string;
  price_aed: number; // the per-PERIOD price actually billed (monthly OR annual)
  interval: string; // "month" | "year"
  /** Stripe collection method: "charge_automatically" (card via Billing
   *  Portal) | "send_invoice" (mid-market bank-transfer / net-30) | "". */
  collection_method: string;
  status: SubscriptionStatus;
  active: boolean;
  trial_end: number; // unix seconds, 0 if none
  period_end: number; // unix seconds, 0 if none
  cancel_at_period_end: boolean;
  billing_configured: boolean; // platform Stripe wired on the server
  can_manage: boolean; // card customer + billing configured (invoice = false)
}

/** True when this tenant pays by invoice / bank transfer (net-30), not card. */
export function isInvoiceBilled(
  info: Pick<SubscriptionInfo, "collection_method"> | null | undefined,
): boolean {
  return info?.collection_method === "send_invoice";
}

/** "Billed monthly" / "Billed annually" for the given interval. */
export function intervalLabel(interval: string): string {
  return interval === "year" ? "Billed annually" : "Billed monthly";
}

/** Human label for how the tenant pays. */
export function paymentMethodLabel(
  info: Pick<SubscriptionInfo, "collection_method"> | null | undefined,
): string {
  return isInvoiceBilled(info) ? "Invoice / bank transfer (net-30)" : "Card";
}

/** Current workspace's platform-subscription status. */
export async function getSubscription(): Promise<SubscriptionInfo> {
  return api<SubscriptionInfo>("/api/billing/subscription");
}

/**
 * Open Stripe's hosted Billing Portal (add/change card, switch plan, cancel).
 * Returns the URL to redirect to. `returnUrl` defaults server-side to the
 * Billing settings page.
 */
export async function openBillingPortal(returnUrl?: string): Promise<string> {
  const res = await api<{ url: string }>("/api/billing/portal", {
    method: "POST",
    body: returnUrl ? { return_url: returnUrl } : {},
  });
  return res.url;
}

// ── §auto-recharge ──────────────────────────────────────────────────

export interface AutoRecharge {
  enabled: boolean;
  threshold_aed: number;
  amount_aed: number;
  monthly_cap_aed: number;
  /** A card is saved and reusable — set by completing a top-up checkout. */
  has_card: boolean;
  /** Stopped itself after repeated declines or a zero balance; needs a human. */
  disarmed: boolean;
  last_error: string;
  last_charge_at: number;
  consecutive_failures: number;
  /** Platform kill switch. False → the toggle cannot do anything yet. */
  platform_enabled: boolean;
}

/** Open a Stripe-HOSTED page to buy credit. The card is typed on Stripe's
 *  domain — no card number ever reaches this app. Returns the URL to send the
 *  owner to. */
export async function startTopupCheckout(
  amountAed: number, saveCard = true,
): Promise<string> {
  const res = await api<{ url: string }>("/api/billing/topup/checkout", {
    method: "POST",
    body: { amount_aed: amountAed, save_card: saveCard },
  });
  return res.url;
}

export async function getAutoRecharge(): Promise<AutoRecharge> {
  return api<AutoRecharge>("/api/billing/auto-recharge");
}

export async function saveAutoRecharge(
  patch: Partial<Pick<AutoRecharge, "enabled" | "threshold_aed" | "amount_aed" | "monthly_cap_aed">>,
): Promise<AutoRecharge> {
  return api<AutoRecharge>("/api/billing/auto-recharge", { method: "PUT", body: patch });
}

/** Days left in a trial (>= 0), or null if not trialing / no trial end. */
export function trialDaysLeft(info: Pick<SubscriptionInfo, "status" | "trial_end">): number | null {
  if (info.status !== "trialing" || !info.trial_end) return null;
  const ms = info.trial_end * 1000 - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

/**
 * The banner state for the dashboard. `null` = nothing to show (no subscription
 * on file, or healthy + not near trial end). Surfacing only — never blocks.
 */
export function billingNotice(
  info: SubscriptionInfo | null | undefined,
): { tone: "info" | "warn" | "danger"; text: string } | null {
  if (!info || !info.status) return null;
  const invoice = isInvoiceBilled(info);
  if (info.status === "trialing") {
    const left = trialDaysLeft(info);
    if (left === null) return null;
    if (left <= 3) {
      // Invoice tenants don't add a card — they get a bank-transfer invoice.
      const action = invoice
        ? "Your first invoice will be sent to your billing email."
        : "Add a payment method to avoid interruption.";
      const today = invoice
        ? "Your free trial ends today — your first invoice is on its way."
        : "Your free trial ends today — add a payment method to keep your agent running.";
      return {
        tone: "warn",
        text:
          left <= 0
            ? today
            : `Your free trial ends in ${left} day${left === 1 ? "" : "s"}. ${action}`,
      };
    }
    return null;
  }
  if (info.status === "past_due" || info.status === "unpaid") {
    return {
      tone: "danger",
      text: invoice
        ? "Your last invoice is overdue. Please complete the bank transfer to keep your agent running."
        : "Your last payment didn't go through. Update your card to keep your agent running.",
    };
  }
  if (info.status === "paused") {
    return {
      tone: "danger",
      text: invoice
        ? "Your subscription is paused. Contact us to settle the outstanding invoice and resume."
        : "Your subscription is paused. Add a payment method to resume your agent.",
    };
  }
  if (info.status === "canceled" || info.status === "incomplete_expired") {
    return { tone: "danger", text: "Your subscription has ended. Re-subscribe to keep using ORIKIN AI." };
  }
  return null;
}

// ─── §usage-billing (2026-07-12) — metered usage, credits, cancellation ──────

export interface UsageChannelSplit {
  units: number;
  cost_aed: number;
}

export interface UsageSummary {
  tenant_id: string;
  cycle: {
    start: string;
    end: string;
    source: "subscription" | "calendar";
    next_charge_at: string;
    cancel_at_period_end: boolean;
    subscription_status: string;
  };
  plan: { name: string; price_aed: number };
  pricing: {
    call_aed_per_min: number;
    chat_aed_per_channel_day: number;
    marketing_aed_per_message: number;
    marketing_addon_enabled: boolean;
    /** "bundle" = flat monthly fee + included WA quota + overage;
     * "metered" = pure per-message. */
    marketing_mode: "bundle" | "metered";
    marketing_bundle_aed: number;
    marketing_bundle_included_msgs: number;
  };
  allowance: {
    included_aed: number;
    wallet_aed: number;
    available_aed: number;
    consumed_aed: number;
    remaining_aed: number;
    pct_used: number;
    warning: "ok" | "warn" | "critical";
    /** No plan allowance + no credits (grandfathered workspace) — usage
     * is tracked for reporting; limits activate with a plan/top-up. */
    unmetered: boolean;
  };
  /** §usage-gate — the enforcement state the sweep last wrote. Absent when the
   *  gate module is unavailable; the page must render fine without it. */
  gate?: {
    state: "ok" | "warn80" | "warn95" | "over" | "blocked";
    grace_until: number;
    enforced: boolean;
    blocking: boolean;
  };
  calls: {
    count: number;
    total_seconds: number;
    billable_minutes: number;
    cost_aed: number;
    by_day: Record<string, number>;
  };
  chats: {
    units: number;
    cost_aed: number;
    by_channel: Record<string, UsageChannelSplit>;
    by_day: Record<string, number>;
  };
  marketing: {
    messages: number;
    included_msgs: number;
    overage_msgs: number;
    cost_aed: number;
    enabled: boolean;
  };
}

export interface UsageCallRow {
  id: string;
  started_at: string;
  caller_number: string;
  direction: string;
  duration_seconds: number;
  billable_minutes: number;
  cost_aed: number;
}

export interface CreditTopup {
  id: string;
  amount_aed: number;
  note: string;
  source: string;
  created_at: string;
}

export interface PlatformInvoice {
  id: string;
  number: string;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string;
  invoice_pdf: string;
}

export const CANCEL_REASONS: { value: string; label: string }[] = [
  { value: "too_expensive", label: "It's too expensive" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "not_enough_usage", label: "Not using it enough" },
  { value: "switching_provider", label: "Switching to another provider" },
  { value: "closing_business", label: "Closing / pausing the business" },
  { value: "other", label: "Something else" },
];

export async function getUsageSummary(): Promise<UsageSummary> {
  return api<UsageSummary>("/api/billing/usage");
}

export async function getUsageCalls(limit = 100): Promise<{
  calls: UsageCallRow[];
  count: number;
  billable_minutes: number;
  cost_aed: number;
}> {
  return api(`/api/billing/usage/calls?limit=${limit}`);
}

export async function getTopups(): Promise<{ topups: CreditTopup[] }> {
  return api("/api/billing/topups");
}

export async function getInvoices(): Promise<{
  invoices: PlatformInvoice[];
  configured: boolean;
}> {
  return api("/api/billing/invoices");
}

export async function cancelSubscription(
  reason: string,
  details: string,
): Promise<{ ok: boolean; cancel_at_period_end: boolean; period_end: number }> {
  return api("/api/billing/cancel", {
    method: "POST",
    body: { reason, details },
  });
}

export async function undoCancelSubscription(): Promise<{
  ok: boolean;
  cancel_at_period_end: boolean;
}> {
  return api("/api/billing/cancel/undo", { method: "POST", body: {} });
}
